import React, { useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, Box, Button, Collapse, Stack } from '@mui/material'
import { com } from '@eidu/form'
import EntityTypeRepository from '../../io/EntityTypeRepository'
import LoadingOverlay from '../../components/feedback/LoadingOverlay'
import FieldsStack from '../../components/data/FieldsStack'
import { requireNotUndefinedOrNull } from '../../util/require'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import entityTypeIdFromString = com.eidu.sharedlib.entity.type.entityTypeIdFromString

type ViewEntityTypePageContentProps = {
  entityType: EntityType
  entityTypes: readonly EntityType[]
  onEdit: () => void
}

const ViewEntityTypePageContent = ({ entityType, entityTypes, onEdit }: ViewEntityTypePageContentProps) => (
  <Stack padding={3} spacing={2}>
    {entityType && <FieldsStack entityType={entityType} entityTypes={entityTypes} onEdit={onEdit} />}
  </Stack>
)

type ViewEntityTypePageProps = {
  entityTypeId: EntityTypeId
  entityTypeRepository: EntityTypeRepository
}

const ViewEntityTypePage = ({ entityTypeId, entityTypeRepository }: ViewEntityTypePageProps) => {
  const navigate = useNavigate()

  const entityType = entityTypeRepository.useItem(entityTypeId, [entityTypeId]) || undefined
  const entityTypes = entityTypeRepository.useSortedEntityTypes() || []
  const error = entityTypeRepository.useError()
  const loading = entityTypeRepository.useLoading()

  return (
    <Stack>
      <Collapse in={!!error}>
        <Alert
          variant="filled"
          severity="error"
          sx={{ margin: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        >
          <Box>{String(error)}</Box>
          <Box>Please reload the page and try again.</Box>
        </Alert>
      </Collapse>
      {entityType && (
        <ViewEntityTypePageContent
          entityType={entityType}
          entityTypes={entityTypes}
          onEdit={() => navigate('edit', { relative: 'path' })}
        />
      )}
      <LoadingOverlay isOpen={!error && loading} />
    </Stack>
  )
}

const ViewEntityTypePageRoute = () => {
  const params = useParams()
  const entityTypeId = entityTypeIdFromString(
    requireNotUndefinedOrNull(params.entityTypeId, 'Entity type ID is required')
  )
  const entityTypeRepository = useContext(EntityTypesContext)

  return <ViewEntityTypePage entityTypeId={entityTypeId} entityTypeRepository={entityTypeRepository} />
}

export default ViewEntityTypePageRoute
